import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches } from '@/Pages/Operations/shared';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import ShiftDialog from './ShiftDialog';
import {
  addDaysIso,
  apiError,
  employeeName,
  formatWallTime,
  isoToday,
  ShiftSwapStatus,
  type EmployeeRef,
  type Roster,
  type ScheduledShift,
  type ShiftSwapRequest,
} from './schedulingLib';

/**
 * Open shifts nobody has claimed, and swap requests waiting on a decision —
 * the two ways a published roster changes after the fact. `scheduling.view`
 * reads both lists; `scheduling.swap_approve` is required to decide a swap,
 * a narrower key than `scheduling.manage` on purpose (ADR-0008 §5): building
 * the rota and signing off somebody else's change to it are different acts.
 */
export default function SwapsTab() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canApprove = can('scheduling.swap_approve');
  const branches = useBranches();

  const [branchId, setBranchId] = useState('');
  const [employees, setEmployees] = useState<EmployeeRef[]>([]);

  const [openShifts, setOpenShifts] = useState<ScheduledShift[]>([]);
  const [openLoading, setOpenLoading] = useState(true);
  const [openError, setOpenError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<ScheduledShift | null>(null);

  const [status, setStatus] = useState<ShiftSwapStatus | ''>(ShiftSwapStatus.PENDING);
  const [swaps, setSwaps] = useState<ShiftSwapRequest[]>([]);
  const [swapsLoading, setSwapsLoading] = useState(true);
  const [swapsError, setSwapsError] = useState<string | null>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [targetId, setTargetId] = useState('');

  useEffect(() => {
    if (!branchId && branches.length > 0) setBranchId(branches[0].id);
  }, [branchId, branches]);

  useEffect(() => {
    api
      .get('/employees', { params: { branchId: branchId || undefined, status: 'ACTIVE', limit: 200 } })
      .then(({ data }) => setEmployees((data?.data ?? []) as EmployeeRef[]))
      .catch(() => setEmployees([]));
  }, [branchId]);

  const loadOpen = useCallback(() => {
    if (!branchId) {
      setOpenLoading(false);
      setOpenShifts([]);
      return;
    }
    setOpenLoading(true);
    setOpenError(null);
    api
      .get<Roster>('/scheduling/roster', {
        params: { branchId, from: isoToday(), to: addDaysIso(isoToday(), 30), openOnly: true },
      })
      .then(({ data }) => setOpenShifts(data.days.flatMap((d) => d.shifts)))
      .catch((e) => setOpenError(apiError(e, t('scheduling.swaps.openLoadFailed'))))
      .finally(() => setOpenLoading(false));
  }, [branchId, t]);

  const loadSwaps = useCallback(() => {
    setSwapsLoading(true);
    setSwapsError(null);
    api
      .get<ShiftSwapRequest[]>('/scheduling/swaps', {
        params: { branchId: branchId || undefined, status: status || undefined },
      })
      .then(({ data }) => setSwaps(data ?? []))
      .catch((e) => setSwapsError(apiError(e, t('scheduling.swaps.loadFailed'))))
      .finally(() => setSwapsLoading(false));
  }, [branchId, status, t]);

  useEffect(loadOpen, [loadOpen]);
  useEffect(loadSwaps, [loadSwaps]);

  const startDecide = (swap: ShiftSwapRequest) => {
    setDecidingId(swap.id);
    setNote('');
    setTargetId(swap.targetEmployeeId ?? '');
  };

  const approve = async (swap: ShiftSwapRequest) => {
    if (!swap.targetEmployeeId && !targetId) return;
    try {
      await api.post(`/scheduling/swaps/${swap.id}/approve`, {
        targetEmployeeId: swap.targetEmployeeId ? undefined : targetId,
        note: note.trim() || undefined,
      });
      toast.success(t('scheduling.swaps.approved'));
      setDecidingId(null);
      loadSwaps();
      loadOpen();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.swaps.decideFailed')));
    }
  };

  const reject = async (swap: ShiftSwapRequest) => {
    try {
      await api.post(`/scheduling/swaps/${swap.id}/reject`, { note: note.trim() || undefined });
      toast.success(t('scheduling.swaps.rejected'));
      setDecidingId(null);
      loadSwaps();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.swaps.decideFailed')));
    }
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="p-4">
          {branches.length > 0 && (
            <Field label={t('scheduling.roster.branch')}>
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </CardContent>
      </Card>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">{t('scheduling.swaps.openShifts')}</h2>
        {openLoading ? (
          <LoadingState label={t('common.loading')} />
        ) : openError ? (
          <ErrorState message={openError} onRetry={loadOpen} />
        ) : openShifts.length === 0 ? (
          <EmptyState title={t('scheduling.swaps.noOpenShifts')} hint={t('scheduling.swaps.noOpenShiftsHint')} />
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {openShifts.map((shift) => (
              <Card key={shift.id}>
                <CardContent className="space-y-1 p-3">
                  <p className="text-sm font-medium tabular-nums">{shift.businessDate}</p>
                  <p className="text-sm tabular-nums">
                    {formatWallTime(shift.startsAt, shift.timezone, i18n.language)}–
                    {formatWallTime(shift.endsAt, shift.timezone, i18n.language)}
                  </p>
                  <p className="text-sm text-ink-muted">{shift.role?.name ?? t('scheduling.shift.noRole')}</p>
                  {can('scheduling.manage') && (
                    <Button variant="outline" size="sm" onClick={() => setAssigning(shift)}>
                      {t('scheduling.swaps.assign')}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">{t('scheduling.swaps.requests')}</h2>
          <Field label={t('scheduling.swaps.status')} controlId="swap-status">
            <Select value={status} onChange={(e) => setStatus(e.target.value as ShiftSwapStatus | '')}>
              <option value="">{t('scheduling.swaps.anyStatus')}</option>
              {Object.values(ShiftSwapStatus).map((s) => (
                <option key={s} value={s}>
                  {t(`scheduling.swapStatus.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {swapsLoading ? (
          <LoadingState label={t('common.loading')} />
        ) : swapsError ? (
          <ErrorState message={swapsError} onRetry={loadSwaps} />
        ) : swaps.length === 0 ? (
          <EmptyState title={t('scheduling.swaps.empty')} hint={t('scheduling.swaps.emptyHint')} />
        ) : (
          <div className="space-y-2">
            {swaps.map((swap) => (
              <Card key={swap.id}>
                <CardContent className="space-y-2 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">
                        {employeeName(swap.requestedBy)} → {swap.target ? employeeName(swap.target) : t('scheduling.swaps.openToAnyone')}
                      </p>
                      <p className="text-xs text-ink-muted tabular-nums">
                        {swap.shift.businessDate} · {formatWallTime(swap.shift.startsAt, swap.shift.timezone, i18n.language)}–
                        {formatWallTime(swap.shift.endsAt, swap.shift.timezone, i18n.language)}
                      </p>
                    </div>
                    <Badge tone={toneForStatus(swap.status)}>{t(`scheduling.swapStatus.${swap.status}`)}</Badge>
                  </div>
                  {swap.reason && <p className="text-sm text-ink-muted">{swap.reason}</p>}

                  {canApprove && swap.status === ShiftSwapStatus.PENDING && (
                    <div className="flex flex-wrap items-center gap-1.5 border-t pt-2">
                      {decidingId === swap.id ? (
                        <>
                          {!swap.targetEmployeeId && (
                            <Select
                              className="h-9 w-48"
                              value={targetId}
                              onChange={(e) => setTargetId(e.target.value)}
                            >
                              <option value="">{t('scheduling.swaps.pickTarget')}</option>
                              {employees
                                .filter((e) => e.id !== swap.requestedBy.id)
                                .map((e) => (
                                  <option key={e.id} value={e.id}>
                                    {e.firstName} {e.lastName ?? ''}
                                  </option>
                                ))}
                            </Select>
                          )}
                          <Input
                            className="h-9 w-40"
                            placeholder={t('scheduling.swaps.notePlaceholder')}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                          />
                          <Button
                            size="sm"
                            disabled={!swap.targetEmployeeId && !targetId}
                            onClick={() => void approve(swap)}
                          >
                            {t('scheduling.swaps.approve')}
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => void reject(swap)}>
                            {t('scheduling.swaps.reject')}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDecidingId(null)}>
                            {t('common.cancel')}
                          </Button>
                        </>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => startDecide(swap)}>
                          {t('scheduling.swaps.decide')}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {assigning && (
        <ShiftDialog
          open
          onClose={() => setAssigning(null)}
          scheduleId={assigning.scheduleId}
          date={assigning.businessDate}
          shift={assigning}
          employees={employees}
          roles={[]}
          templates={[]}
          canManage={can('scheduling.manage')}
          onSaved={loadOpen}
        />
      )}
    </div>
  );
}
